/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {PersonType} from "types/enums/PersonType";
import {Experience} from "types/enums/Experience";
import {Package} from "types/entities/Package";
import {Company} from "types/entities/Company";
import {Timeslot} from "types/entities/Timeslot";
import {PackagesCart} from "types/entities/PackagesCart";
import {PackageUpsell, PaymentAssert, ReservationNote} from "store/booking/BookingAction";
import {Payment} from "types/entities/Payment";
import {Voucher} from "types/entities/Voucher";

import {Profile} from "./entities/Profile";
import {PackageCategory} from "types/enums/PackageCategory";
import {PackagesItemByPerson, PackagesItemsResponse} from "types/entities/PackagesItems";

export type AdultOrChild = "adult" | "child";

export interface BookingTimeslot {
    timeslots: Timeslot[];
}

export interface Hall {
    id: number;
    hall_code: string;
    category_id: number | null;
    name: string;
    image: string;
    email_from: string;
    email: string;
    contact: string;
    address: string;
    hotline: string;
    ticket_header: string;
    domain: string;
    ticket_footer: string;
    ticket_qrinfo: string;
    specifications: string;
    description: string;
    api_url: string;
    online_visitors: number;
    disk_used: number;
    disk_free: number;
    cpu0: number;
    cpu1: number;
    cpu2: number;
    queue_new: number;
    queue_old: number;
    transactions_count: number;
    ping: number;
    timestamp_added: number;
    timestamp_last_update: number;
    drivers?: Driver[] | null;
    rankings?: Ranking[] | null;
}

export interface Ranking {
    id: number;
    hall_id: number | null;
    driver_id: number | null;
    lap_time: number | null;
    position: number;
    driver?: Driver | null;
    hall?: Hall | null;
}

export interface ReferralsConvertData {
    referral_codes: string[];
}

export interface ReferralClaimData {
    code: string;
    claimed_driver_id: number;
}

export enum ReferralsType {
    Pending = "pending",
    Registered = "registered",
    Success = "success",
    Convert = "convert",
}

export enum ReferralStatus {
    New = "new", //is created
    Viewed = "viewed", // is viewed by target
    Shared = "shared", // is viewed by target
    Confirmed = "confirmed", // is confirmed -> rule is satisfied
    Earned = "earned", // is earned
    Converted = "converted", // is converted to vouchher
    Expired = "expired", //has expired
    Invalid = "invalid", // set invalid
}

export interface Referral {
    id: number;
    guid: string;
    code: string;
    driver_id: number;
    claimed_driver_id: number | null;
    driver: Profile | Driver;
    claimedDriver?: Profile | Driver;
    referralRule: ReferralRule;
    status: ReferralStatus;
    viewed_at: string;
    accepted_at: string;
    expires_at: string;
    created_at: string;
    updated_at: string;
}

export interface ReferralRule {
    id: number;
    hall_id: number;
    guid: string;
    valid_at: string;
    expires_at: string;
    credit_referrer: number;
    credit_referred: number;
    credit_kickback_referrer: number;
    credit_kickback_referred_spend_minimum: number;
    created_at: string;
    updated_at: string;
    hall: Hall;
}

export interface Driver {
    identifier: number;
    name: string;
    last_name: string;
    username: string;
    profile_picture_url: string | null;
    experience: Experience;
    person_type: PersonType;
    selected: boolean;
    races_count: number;
    races_assigned_count: number;
}

export interface ReservationSlot {
    identifier: number;
    date: string;
    hour: number;
    minute: number;
    selected?: boolean;
    drivers: any;
}

export interface BookingProcess {
    filter: BookingFilter;
    package: Package | null;
    upsell: PackageUpsell[];
    packageCart: PackagesCart | null;
    voucher: Voucher | null;
    payment: Payment | null;
    paymentAssert: PaymentAssert | null;
    customer: Company | null;
    notes: ReservationNote[];
    possibleTimeslots: Timeslot[];
    customTimeslots: Timeslot[];
    dateChosenByCustomer: boolean;
    emailVerified: boolean;
    emailLogin: boolean;
    selectedTimeslot: Timeslot | null;
    availablePackageItems: PackagesItemsResponse | null;
    selectedPackageItems: PackagesItemByPerson[];
}

export enum groupType {
    None = 0,
    Company = 1,
    Bachelor = 2,
    Children = 3,
    Coupons = 4,
}

export interface BookingFilter {
    adults: number;
    children: number;
    date: string;
    activity_ids: number[];
    groupType: string[];
    hours_count?: number;
    category?: PackageCategory | null;
}

export interface PaginationLink {
    url: string;
    label: string;
    active: boolean;
}

export interface PaginatedRows<T> {
    data: T[];
    current_page: number;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLink[];
    next_page_url: string | null;
    path: string;
    per_page: string;
    prev_page_url: string | null;
    to: number;
    total: number;
}
